/**
 * AppBar — the fixed top bar shared by CCA and EIA.
 *
 * Layout: AMD logo lockup | divider | product name | section nav.
 *
 * One bar, both products. The product name comes from the resolved variant
 * ('Enterprise Cloud Cost Advisor' vs 'Enterprise EPYC Instance Advisor') and
 * the links come from getNavItems(mode), so CCA and EIA can expose different
 * sections without forking this component.
 *
 * It is position:fixed (not sticky) so it never scrolls away. Fixed elements
 * leave no space behind them, so the bar also renders a spacer of equal height
 * — that keeps the offset correct without the layout hard-coding a magic
 * padding value that silently breaks when the bar's height changes.
 */
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { env } from '@config/env';
import { getNavItems } from '@config/navigation';
import { safeHttpUrl } from '@features/auth';
import { cx } from '@lib/classNames';
import { PrefetchLink } from '@router/PrefetchLink';
import { selectIsAuthenticated, selectRoles } from '@security/auth/authSlice';
import { useAppMode } from '@variant/useAppMode';

import { HeaderActions } from './HeaderActions';
import { APP_BAR_LOGOS } from './logos';

import styles from './AppBar.module.css';

/**
 * Is `path` the active route? Prefix match by default, so a detail page
 * (/explorer/abc) keeps its parent link lit; exact match when the item sets
 * `end` (for a path that is a prefix of its own children).
 */
function isActivePath(currentPath, path, end) {
  if (end) return currentPath === path;
  return currentPath === path || currentPath.startsWith(`${path}/`);
}

/**
 * The AMD lockup: wordmark over the product short name. Swap the inner markup
 * for an <img src={logoSrc} alt="AMD EPYC" /> when the real asset lands — the
 * surrounding classes already reserve the right box.
 */
/**
 * The logo's link. VITE_ADVISORY set (http/https only, via safeHttpUrl) -> a
 * plain link to that site in the same tab, as legacy's homeBtnClick left with
 * window.location.href. Unset or invalid -> the landing Home page ('/'; the
 * router adds the local /cca or /eia prefix).
 */
function LogoLink({ label, children }) {
  const href = safeHttpUrl(env.advisoryUrl);
  if (href) {
    return (
      <a href={href} className={styles.logo} aria-label={`${label} — Home`}>
        {children}
      </a>
    );
  }
  return (
    <Link to="/" className={styles.logo} aria-label={`${label} — Home`}>
      {children}
    </Link>
  );
}

function BrandLogo({ mode, shortName }) {
  const src = APP_BAR_LOGOS[mode];
  const label = `AMD EPYC ${shortName}`;

  // Real artwork where we have it. The text lockup below is the fallback for a
  // variant with no asset yet — drop its SVG into APP_BAR_LOGOS and it is used
  // automatically, no change here.
  if (src) {
    return (
      <LogoLink label={label}>
        <img className={styles.logoImage} src={src} alt="" />
      </LogoLink>
    );
  }

  return (
    <LogoLink label={label}>
      <span className={styles.logoWordmark} aria-hidden="true">
        AMD
      </span>
      <span className={styles.logoSub} aria-hidden="true">
        EPYC <b>{shortName}</b>
      </span>
    </LogoLink>
  );
}

export function AppBar({ items, className }) {
  const { mode, variant } = useAppMode();
  const { pathname } = useLocation();
  const roles = useSelector(selectRoles);
  const isAuthed = useSelector(selectIsAuthenticated);

  // `items` is an escape hatch for tests/storybook; normally the bar derives
  // its own list from the mode so callers can't drift from the route table.
  //
  // Roles are passed so a permission-gated section (Analytics) is hidden from
  // anyone the route guard would turn away — a visible link that 403s on click
  // is worse than no link.
  //
  // Signed out (the Advisory Home page is the one place the bar shows then),
  // there are no sections at all, as in legacy's signed-out header: every one
  // of them is behind RequireAuth and would only bounce back here.
  const navItems = items ?? (isAuthed ? getNavItems(mode, roles) : []);

  return (
    <>
      <header className={cx(styles.bar, className)}>
        <div className={styles.brand}>
          <BrandLogo mode={mode} shortName={variant.shortName} />
          <span className={styles.divider} aria-hidden="true" />
          <span className={styles.product}>{variant.productName}</span>
        </div>

        <nav className={styles.nav} aria-label="Primary">
          {navItems.map((item) => {
            const active = isActivePath(pathname, item.path, item.end);
            return (
              <PrefetchLink
                key={item.path}
                to={item.path}
                preload={item.page}
                className={cx(styles.link, active && styles.linkActive)}
                aria-current={active ? 'page' : undefined}
              >
                {item.title}
              </PrefetchLink>
            );
          })}
        </nav>

        <HeaderActions />
      </header>

      {/* Reserves the fixed bar's height in normal flow. */}
      <div className={styles.spacer} aria-hidden="true" />
    </>
  );
}

export default AppBar;
